/**
 * The storage tiers a person chooses between on Amazon S3, Google Cloud
 * Storage and Azure Blob Storage, each as one value: the catalogue's facts
 * about the class, its price row, what restoring 1 TB from it costs, and
 * whether a new vault can be created on it yet. Read by the portal's
 * /providers comparison, and meant for the desktop's storage tier picker too
 * (#762), so the two show the same rows.
 */

import {
  type HyperscalerProvider,
  HYPERSCALER_PROVIDERS,
  type RetrievalOption,
  type StorageTierPrice,
  lookupStorageTierPrice,
} from "./hyperscalerPricing";
import { type CreateVaultAnswer, type StorageClass, canCreateVaultOn, storageClassesOf } from "./storageClasses";

/** Gigabytes to the terabyte, as the price table counts them. */
const GB_PER_TB = 1000;

/**
 * Bytes to the gigabyte. Decimal, as the price table's terabyte is: where a
 * provider bills by the GiB this counts a few more gigabytes than the bill
 * does, so the estimate errs high, never low.
 */
const BYTES_PER_GB = 1e9;

/**
 * Days a retrieved copy is kept and billed before it goes: Amazon S3 deletes
 * its own, and on Azure Big Bear deletes it (ADR 0026). Mirrors
 * `RETRIEVED_COPY_DAYS` in the store crate's `lib.rs`, which the engine asks
 * for it; `scripts/check-engine-constants.mjs` holds the two equal.
 */
export const RETRIEVED_COPY_DAYS = 7;

/** Days to the month, for prorating the temporary copy's monthly rate. */
const DAYS_PER_MONTH = 30;

/** What a restore will read, as the engine's restore plan counts it. */
export interface RestoreSize {
  /** Bytes in the data packs still to retrieve: archived, not yet readable or in progress. */
  bytesToRetrieve: number;
  /** How many data packs those are; each is one retrieval request. */
  packsToRetrieve: number;
  /** Every byte the restore reads: the packs to retrieve and those already readable. */
  bytesToRead: number;
}

/** What a restore will cost and wait, each part on its own, at the reference region. */
export interface RestoreEstimate {
  retrievalOption: RetrievalOption;
  /** Dollars to retrieve: the per-GB fee on the bytes to retrieve, plus the per-request fee for each pack. */
  retrievalFee: number;
  /** Dollars to keep the retrieved copy for `RETRIEVED_COPY_DAYS`. */
  keepingCopy: number;
  /** Dollars of egress on every byte read, with no free allowance taken off. */
  egress: number;
  /** The three parts added up. */
  total: number;
  /**
   * Gigabytes a month the provider moves out free. Not subtracted from
   * `egress`, because it is shared with everything else on the account that
   * month and the app can't see how much is left; carried so a screen can say
   * the egress may come out lower.
   */
  freeEgressGbPerMonth: number;
  /** How long the retrieval typically takes, in words. */
  typicalWait: string;
  /** The region these prices are for; other regions differ. */
  referenceRegion: string;
  /** The month the prices were checked against the provider's own. */
  checked: string;
}

/**
 * What restoring from a storage tier will cost with one retrieval option,
 * sized from the restore plan. Packs already readable belong in
 * `bytesToRead` only, so they add egress and nothing else.
 *
 * A class with no retrieval options in the price table has one way to be
 * read, and Standard prices it from the row's own figures: no request fee
 * and no copy kept. Bulk on such a class, or on one without Bulk (Azure
 * Archive), throws.
 */
export function restoreEstimate(price: StorageTierPrice, option: RetrievalOption, size: RestoreSize): RestoreEstimate {
  const retrieval = price.retrieval;
  const fees = retrieval?.[option] ?? null;
  if (option !== "standard" && !fees) {
    throw new Error(`${price.provider} ${price.requestValue} has no Bulk retrieval option to estimate.`);
  }

  const gbToRetrieve = size.bytesToRetrieve / BYTES_PER_GB;
  const retrievalFee = fees
    ? gbToRetrieve * fees.perGb + (size.packsToRetrieve / 1000) * fees.perThousandRequests
    : gbToRetrieve * price.retrievalPerGb;
  const keepingCopy = retrieval
    ? gbToRetrieve * (retrieval.temporaryCopyPerTbMonth / GB_PER_TB) * (RETRIEVED_COPY_DAYS / DAYS_PER_MONTH)
    : 0;
  const egress = (size.bytesToRead / BYTES_PER_GB) * (price.egressPerTb / GB_PER_TB);

  return {
    retrievalOption: option,
    retrievalFee,
    keepingCopy,
    egress,
    total: retrievalFee + keepingCopy + egress,
    freeEgressGbPerMonth: price.freeEgressGbPerMonth,
    typicalWait: fees ? fees.typicalWait : price.timeToFirstByte,
    referenceRegion: price.referenceRegion,
    checked: price.checked,
  };
}

export interface StorageTierChoice {
  /** The catalogue's row: the provider's own name, the minimum storage duration, and whether a read waits on a retrieval. */
  storageClass: StorageClass;
  price: StorageTierPrice;
  /** Dollars to read 1 TB back out at Standard: the retrieval fee, the copy kept, and egress, at the reference region. */
  restoreOneTb: number;
  /** Whether a new vault can be created on this storage tier yet, and why not. */
  create: CreateVaultAnswer;
}

/**
 * The data pack size on a storage tier whose reads wait on a retrieval, in
 * MiB: what 1 TB is counted out in for its retrieval requests. Mirrors the
 * format crate's `DELAYED_TARGET_PACK_SIZE`; `scripts/check-engine-constants.mjs`
 * holds the two equal.
 */
export const DELAYED_PACK_SIZE_MIB = 256;

/** 1 TB, as a restore plan would size it: every byte to retrieve and to read. */
const ONE_TB: RestoreSize = {
  bytesToRetrieve: GB_PER_TB * BYTES_PER_GB,
  packsToRetrieve: Math.ceil((GB_PER_TB * BYTES_PER_GB) / (DELAYED_PACK_SIZE_MIB * 1024 * 1024)),
  bytesToRead: GB_PER_TB * BYTES_PER_GB,
};

/**
 * What reading 1 TB back out of a class costs, to the cent: the restore
 * estimate for 1 TB at Standard, so the two figures can't disagree. That is
 * its retrieval fee on 1,000 GB, plus on a class that waits on a retrieval
 * a request for each data pack and seven days of the retrieved copy, plus a
 * terabyte of egress.
 * The free egress allowance is left out — it is shared with everything else
 * on the account that month, so the app cannot know how much of it is left.
 */
export function costToRestoreOneTb(price: StorageTierPrice): number {
  return Math.round(restoreEstimate(price, "standard", ONE_TB).total * 100) / 100;
}

/** Every class of one provider, in the catalogue's order. */
export function storageTierChoices(provider: HyperscalerProvider): StorageTierChoice[] {
  return storageClassesOf(provider).map((storageClass) => {
    const price = lookupStorageTierPrice(provider, storageClass.requestValue);
    if (!price) throw new Error(`No price row for ${provider} ${storageClass.displayName}.`);
    return { storageClass, price, restoreOneTb: costToRestoreOneTb(price), create: canCreateVaultOn(storageClass) };
  });
}

/** One provider's class, by the catalogue's request value, or none. */
export function storageTierChoice(provider: HyperscalerProvider, requestValue: string): StorageTierChoice | null {
  return storageTierChoices(provider).find((c) => c.storageClass.requestValue === requestValue) ?? null;
}

/**
 * Every class of all three providers, cheapest to store first — the /providers
 * comparison. Ties keep the providers' order (Amazon S3, Google Cloud, Azure).
 */
export function hyperscalerTierChoicesByPrice(): StorageTierChoice[] {
  return HYPERSCALER_PROVIDERS.flatMap((provider) => storageTierChoices(provider)).sort(
    (a, b) => a.price.pricePerTbMonth - b.price.pricePerTbMonth,
  );
}
